import { CircularProgress, styled } from '@mui/material'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { Button } from '../../../../components/buttons'
import moment from 'moment'
import { colorPalette } from '../../../../style/colorPalette'
import {
  LoadingContainer,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  HeaderCell,
  DataCell,
  DialogHeader,
  DialogContent,
  DialogTitle,
  HeaderButtons,
} from './commons'
import { CloseButton, StyledDivider } from '../commons'
import { RenameMenu } from './RenameMenu'
import { memo, useCallback } from 'react'

interface ReportMenuProps {
  templateName: string
  reports: VersionReportVersionEntity[]
  loading: boolean
  onReportSelect: (report: VersionReportVersionEntity) => void
  onReportUpdate: (report: VersionReportVersionEntity) => void
  onBackToTemplate: () => void
  onCreateNewReport: () => void
  onClose: () => void
}

export const ReportMenu = ({
  templateName,
  reports,
  loading,
  onReportSelect,
  onReportUpdate,
  onBackToTemplate,
  onCreateNewReport,
  onClose,
}: ReportMenuProps) => {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{templateName}</DialogTitle>
        <HeaderButtons>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={onBackToTemplate}
            size="s"
          >
            戻る
          </Button>
          <CloseButton onClick={onClose} />
        </HeaderButtons>
      </DialogHeader>
      <StyledDivider />
      <ReportListHeader>
        <ReportListTitle>報告書一覧</ReportListTitle>
        <Button size="s" variant="outlined" onClick={onCreateNewReport}>
          新しく報告書を作成
        </Button>
      </ReportListHeader>
      <StyledDivider />
      <DialogContent>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <TableContainer>
            <TableHeader>
              <HeaderCell width="38%">名称</HeaderCell>
              <HeaderCell width="30%">更新者</HeaderCell>
              <HeaderCell width="25%">更新日時</HeaderCell>
              <HeaderCell width="8%"></HeaderCell>
            </TableHeader>
            <TableBody>
              {reports.map(report => (
                <ReportRow
                  key={report.uuid}
                  report={report}
                  onReportSelect={onReportSelect}
                  onReportUpdate={onReportUpdate}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </DialogContent>
    </>
  )
}

const ReportRow = memo(
  ({
    report,
    onReportSelect,
    onReportUpdate,
  }: {
    report: VersionReportVersionEntity
    onReportSelect: (report: VersionReportVersionEntity) => void
    onReportUpdate: (report: VersionReportVersionEntity) => void
  }) => {
    const handleSelect = useCallback(() => {
      onReportSelect(report)
    }, [report, onReportSelect])

    const handleRename = useCallback(() => {
      onReportUpdate(report)
    }, [report, onReportUpdate])

    return (
      <ReportTableRow>
        <DataCell width="38%" onClick={handleSelect}>
          {report.name}
        </DataCell>
        <DataCell width="30%" onClick={handleSelect}>
          {report.updatedBy.name}
        </DataCell>
        <DataCell width="25%" onClick={handleSelect}>
          {moment(report.updatedAt).format('MM/DD HH:mm')}
        </DataCell>
        <ActionCell width="7%">
          <MenuButtonWrapper className="report-action-menu">
            <RenameMenu onRename={handleRename} label="報告書名を編集する" />
          </MenuButtonWrapper>
        </ActionCell>
      </ReportTableRow>
    )
  }
)

ReportRow.displayName = 'ReportRow'

const ReportTableRow = styled(TableRow)({
  '&:hover .report-action-menu': {
    opacity: 1,
  },
})

const ActionCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}))

const MenuButtonWrapper = styled('div')({
  opacity: 0,
  transition: 'opacity 0.2s',
})

const ReportListHeader = styled('div')({
  padding: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const ReportListTitle = styled('h3')({
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
  color: colorPalette.monotone[10],
})
